import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Lenis from "@studio-freight/lenis";
import homePageSchema from "./schema/homePageSchema";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LanguageSelector from "./components/LanguageSelector";
import FloatingStudentCaseCta from "./components/FloatingStudentCaseCta";

// Pages
import MainPage from "./pages/MainPage";
import Connect from "./pages/Connect";
import Photos from "./pages/Photos";
import MyJourney from "./pages/MyJourney";
import News from "./pages/News";
import NewsClips from "./pages/NewsClips";
import Videos from "./pages/Videos";
import StudentCasePortal from "./pages/StudentCasePortal";
import PRKit from "./pages/PRKit";

import NotFound from "./pages/NotFound";
/* 🔹 Smooth Scroll Wrapper */
function SmoothScroll({ children }) {
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({
      smooth: true,
      lerp: 0.08, // smoothness (0.06–0.1 best)
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });

    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // 🔹 Route change pe top scroll
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [location.pathname]);

  return children;
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <SmoothScroll>
          <div className="min-h-screen flex flex-col">
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify(homePageSchema)}
              </script>
            </Helmet>

            {/* Skip to content (Accessibility bonus) */}
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 bg-black text-white px-4 py-2 z-50"
            >
              Skip to main content
            </a>

            <Header />
            <LanguageSelector />
            <FloatingStudentCaseCta />

            {/* ✅ Main Content Landmark */}
            <main id="main-content" className="flex-grow">
              <Routes>
                <Route path="/" element={<MainPage />} />
                <Route path="/connect/" element={<Connect />} />
                <Route path="/photos/" element={<Photos />} />
                <Route path="/journey/" element={<MyJourney />} />
                <Route path="/news/" element={<News />} />
                <Route path="/news-clips/" element={<NewsClips />} />
                <Route path="/videos/" element={<Videos />} />
                <Route path="/pr-kit/" element={<PRKit />} />
                <Route path="/jharkhand-fir-details/" element={<StudentCasePortal />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>

            <Footer />
          </div>
        </SmoothScroll>
      </Router>
    </HelmetProvider>
  );
}

export default App;
